import { useGetSectionDataQuery, useGetSectionDefinitionsQuery } from 'API/order/fees/FeeApi';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootStore } from 'store/types';
import { Card, PageLoading } from 'ui-toolkit';
import { FeeSections } from './FeeSections';
import { IFeeSection, organizeFees } from './organizeFees';
import { FeesErrorModal } from './FeesErrorModal';
import { assertDefined } from 'utilities/Assertions';
import { PurchaseFees } from './PurchaseFees';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { ReissueDiscountsContainer } from '../discounts/ReissueDiscountsContainer';
import { GeneratePdfModal } from './GeneratePdfModal';
import { UnderlineTabsWrapper } from 'ui-toolkit/components/UnderlineTabs/UnderlineTabsWrapper';
import { StyledFeeSections } from './StyledFeeSections';
import { SECTIONDEFINITION_SEQUENCE_PAYOFFS } from 'types/order/fees/ISectionDefinition';
import { GeneratePdfButton } from './GeneratePdfButton';
import { IncomingFundsPage } from './incomingFunds/IncomingFundsPage';
import { ClientUrlFactory } from 'ui-toolkit/rest/ClientUrlFactory';
import { CashToSection } from './CashToSection';
import { Validation } from './validation/Validation';
import { SpaEscrowDisbursedTransactionsContainer } from '../escrow/SpaEscrowDisbursedTransactionsContainer';
import { DisbursementsButton } from './disburseAll/DisbursementsButton';
import { MiscDisbursementsContainer } from './MiscDisbursementsContainer';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { useHasPermissions } from '../../accessControl/hooks/useHasPermissions';
import { OrderTabs } from 'store/order/orderNavigation/orderNavigationSlice';
import { useListOrderTagsQuery } from 'API/order/tags/OrderTagApi';
import { eTransactionCategory } from '../../../ui-toolkit/enums/eTransactionType';
import { DisbursementCopyContextProvider } from './edit/disbursements/context/DisbursementCopyContext';

export interface IFeesContainerProps {}

export const FeesContainer: React.FC<IFeesContainerProps> = () => {
  const { data: order } = useSelectCurrentOrder();
  const orderId = useSelector((rootStore: RootStore) => rootStore.order.currentOrderSlice.orderId);
  assertDefined(orderId);
  const isRefinance = order.transaction.transaction_category === eTransactionCategory.Refinance;
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const hasDisbursePermissions = useHasPermissions([ePermissions.EditEscrowInitiateDisbursement]);
  const { data: tags = [], isFetching: isTagsFetching } = useListOrderTagsQuery(orderId);

  const {
    isLoading: isLoadingFeeDefinitions,
    data: sectionDefinitionData = [],
    error: feeDefinitionsError,
    isSuccess: isSuccessFeeDefinitions,
  } = useGetSectionDefinitionsQuery();
  const {
    isLoading: isLoadingFees,
    data: sectionData = [],
    error: feesError,
    isSuccess: isSuccessFees,
  } = useGetSectionDataQuery(orderId, { refetchOnMountOrArgChange: true });

  const isLoading = isLoadingFeeDefinitions || isLoadingFees;
  const isSuccess = isSuccessFeeDefinitions || isSuccessFees;

  const feeSections: IFeeSection[] = useMemo(
    () => organizeFees({ sectionDefinitionData, sectionData }),
    [sectionDefinitionData, sectionData]
  );

  const ajSections = useMemo(
    () => feeSections.filter((t) => t.sectionDefinition.sectionSeq <= SECTIONDEFINITION_SEQUENCE_PAYOFFS),
    [feeSections]
  );
  const tabs = [
    {
      tab: { text: `Sections A - J & Payoffs` },
      route: ClientUrlFactory().urlFor.feesTab(orderId),
      content: (
        <StyledFeeSections>
          <FeeSections feeSections={ajSections} />
          <ReissueDiscountsContainer />
          <CashToSection feeSections={feeSections} />
        </StyledFeeSections>
      ),
    },
  ];
  const knSections = useMemo(
    () => feeSections.filter((t) => t.sectionDefinition.sectionSeq > SECTIONDEFINITION_SEQUENCE_PAYOFFS),
    [feeSections]
  );
  if (!isRefinance) {
    tabs.push({
      tab: { text: `Sections K - N` },
      route: ClientUrlFactory().urlFor.feesTab(orderId, 'k-n'),
      content: <PurchaseFees feeSections={knSections} />,
    });
  }
  tabs.push({
    tab: { text: `Incoming Funds` },
    route: ClientUrlFactory().urlFor.feesTab(orderId, 'incoming-funds'),
    content: <IncomingFundsPage orderId={orderId} escrowFileId={order.transaction.escrow_file_id || ''} />,
  });
  tabs.push({
    tab: { text: 'Misc Disbursements' },
    route: ClientUrlFactory().urlFor.feesTab(orderId, 'misc-disbursements'),
    content: <MiscDisbursementsContainer feeSections={feeSections} />,
  });
  tabs.push({
    tab: { text: `Disbursed Transactions` },
    route: ClientUrlFactory().urlFor.feesTab(orderId, 'disbursed-transactions'),
    content: <SpaEscrowDisbursedTransactionsContainer orderId={orderId} tab={OrderTabs.Fees} />,
  });

  return (
    <DisbursementCopyContextProvider>
      {!isTagsFetching && (
        <Validation sections={sectionData} sectionDefinitions={sectionDefinitionData} className={'grid'} tags={tags} />
      )}
      <Card
        title="Fees"
        actions={
          <div className="flex gap-4">
            {hasDisbursePermissions && <DisbursementsButton orderId={orderId} />}
            <GeneratePdfButton orderId={orderId} setShowGenerateModal={setShowGenerateModal} />
          </div>
        }
      >
        {showGenerateModal && <GeneratePdfModal orderId={orderId} onClose={() => setShowGenerateModal(false)} />}
        <PageLoading isLoading={isLoading || !isSuccess}>
          <UnderlineTabsWrapper>{tabs}</UnderlineTabsWrapper>
        </PageLoading>
        <GeneratePdfButton orderId={orderId} setShowGenerateModal={setShowGenerateModal} />
        {feesError && <FeesErrorModal error={feesError} title={'Error Loading Fees Page'} />}
        {feeDefinitionsError && <FeesErrorModal error={feeDefinitionsError} title={'Error Loading Fee Definitions'} />}
      </Card>
    </DisbursementCopyContextProvider>
  );
};
